// src/components/GlobeSearch.js
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "../App.css";
import "./GlobeSearch.css";

import { VISITED_PATHS } from "../data/VisitedPaths";
import { ALL_PLACES } from "../data/AllPlaces";
import { useNavigate } from "react-router-dom";

function GlobeSearch({ onHighlight }) {
  const [query, setQuery] = useState("");
  const [message, setMessage] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    left: 0,
    width: 0
  });

  const navigate = useNavigate();

  // ⭐ Normalize text: lowercase, remove accents, remove spaces
  const normalizeText = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // remove accents
      .replace(/\s+/g, "");

  // ⭐ Build a flat list of all place names for autocomplete
  const allNames = [
    ...ALL_PLACES.world.map((p) => p.name),
    ...ALL_PLACES.puertorico.map((p) => p.name),
    ...ALL_PLACES.usa.map((p) => p.name)
  ];

  // ⭐ Position dropdown under the search bar
  const positionDropdown = () => {
    const input = document.querySelector(".region-search");
    if (!input) return;

    const rect = input.getBoundingClientRect();

    setDropdownPos({
      top: rect.bottom,
      left: rect.left,
      width: rect.width
    });
  };

  const handleInput = (value) => {
    setQuery(value);
    setMessage("");

    if (!value.trim()) {
      setSuggestions([]);
      return;
    }

    const normalizedInput = normalizeText(value);

    const filtered = allNames.filter((name) =>
      normalizeText(name).includes(normalizedInput)
    );

    setSuggestions(filtered.slice(0, 8)); // limit dropdown

    // ⭐ Reposition dropdown after suggestions appear
    setTimeout(positionDropdown, 0);
  };

  const handleSuggestionClick = (name) => {
    setQuery(name);          // ✔ fill input
    setSuggestions([]);      // ✔ close dropdown

    // ⭐ Reposition after closing
    setTimeout(positionDropdown, 0);

    // ⭐ IMPORTANT: allow Enter to work immediately after selecting
    setTimeout(() => {
      const input = document.querySelector(".region-search");
      if (input) input.focus();
    }, 0);
  };

  const handleSearch = () => {
    if (!query.trim()) return;

    const raw = query.trim();
    const normalized = normalizeText(raw);

    // ⭐ 1️⃣ FIRST: If in VisitedPaths → go directly
    if (VISITED_PATHS[normalized]) {
      navigate(VISITED_PATHS[normalized]);

      // ⭐ CLEAR SEARCH BAR AFTER NAVIGATION
      setQuery("");
      setSuggestions([]);

      return;
    }

    // ⭐ 2️⃣ NOT VISITED → search AllPlaces by map group
    let found = null;
    let mapKey = null;

    // WORLD
    found = ALL_PLACES.world.find(
      (p) => normalizeText(p.name) === normalized
    );
    if (found) mapKey = "world";

    // PUERTO RICO
    if (!found) {
      found = ALL_PLACES.puertorico.find(
        (p) => normalizeText(p.name) === normalized
      );
      if (found) mapKey = "puertorico";
    }

    // USA
    if (!found) {
      found = ALL_PLACES.usa.find(
        (p) => normalizeText(p.name) === normalized
      );
      if (found) mapKey = "usa";
    }

    // ⭐ FOUND IN ALL_PLACES → go to correct map + highlight
    if (found) {
      navigate(`/map/${mapKey}`, {
        state: { highlightId: found.id }
      });

      if (onHighlight) onHighlight(found.id);

      // ⭐ CLEAR SEARCH BAR AFTER NAVIGATION
      setQuery("");
      setSuggestions([]);

      return;
    }

    // ⭐ 3️⃣ NOT FOUND ANYWHERE
    setMessage(`${raw} not found`);
  };

  return (
    <>
      <div className="globe-search-wrapper">
        <input
          type="text"
          className="region-search"
          placeholder="Search a place..."
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
        />

        <button
          className="go-button"
          onClick={(e) => {
            e.stopPropagation();
            handleSearch();
          }}
        >
          Go
        </button>

        {message && <div className="search-message">{message}</div>}
      </div>

      {/* ⭐ PORTAL: dropdown rendered OUTSIDE the map/header */}
      {suggestions.length > 0 &&
        createPortal(
          <div
            className="search-suggestions"
            style={{
              top: dropdownPos.top + "px",
              left: dropdownPos.left + "px",
              width: dropdownPos.width + "px"
            }}
          >
            {suggestions.map((name, idx) => (
              <div
                key={idx}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(name)}
              >
                {name}
              </div>
            ))}
          </div>,
          document.getElementById("dropdown-root")
        )}
    </>
  );
}

export default GlobeSearch;
